// Auth context. The portal uses cookie-based sessions: the ac-portal Worker sets
// an httpOnly `ac_portal` cookie on /auth/verify that the browser cannot read from
// JS. So we determine signed-in state by PROBING an authenticated endpoint
// (GET /tickets): a 200 means signed in, a 401 means signed out. Other errors are
// treated as "unknown" but default to signed-out so the sign-in page is shown.
//
// `verify` is called by the /verify route after POST /auth/verify succeeds; on
// success we flip to authenticated without re-probing. `signOut` clears local
// state (the cookie itself is httpOnly + has no portal logout endpoint, so this is
// a client-side reset; see README "Assumptions").

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ApiError, listTickets } from "./api";

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthValue {
  status: AuthStatus;
  /** Re-probe GET /tickets to refresh signed-in state. */
  refresh: () => Promise<void>;
  /** Mark the session authenticated (after a successful /auth/verify). */
  markAuthenticated: () => void;
  /** Client-side sign-out: reset to anonymous. */
  signOut: () => void;
}

const AuthContext = createContext<AuthValue>({
  status: "loading",
  refresh: async () => {},
  markAuthenticated: () => {},
  signOut: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");

  const probe = useCallback(async (signal?: AbortSignal) => {
    try {
      await listTickets(signal);
      if (!signal?.aborted) setStatus("authenticated");
    } catch (err) {
      if (signal?.aborted) return;
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (err instanceof ApiError && err.status === 401) {
        setStatus("anonymous");
        return;
      }
      // Network/other error: assume signed-out (the sign-in page is the safe
      // default and the KB stays reachable regardless).
      setStatus("anonymous");
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void probe(ctrl.signal);
    return () => ctrl.abort();
  }, [probe]);

  const refresh = useCallback(async () => {
    setStatus("loading");
    await probe();
  }, [probe]);

  const markAuthenticated = useCallback(() => setStatus("authenticated"), []);
  const signOut = useCallback(() => setStatus("anonymous"), []);

  const value = useMemo<AuthValue>(
    () => ({ status, refresh, markAuthenticated, signOut }),
    [status, refresh, markAuthenticated, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  return useContext(AuthContext);
}
